import { ReactElement } from 'react';
import type { CloudPosPermSpec } from 'src/Common/permissions/cloudPosPermissions';
// Import all required icons
import HomeTwoToneIcon from '@mui/icons-material/HomeTwoTone';
import PointOfSaleTwoToneIcon from '@mui/icons-material/PointOfSaleTwoTone';
import AssignmentReturnTwoToneIcon from '@mui/icons-material/AssignmentReturnTwoTone';
import SwapHorizTwoToneIcon from '@mui/icons-material/SwapHorizTwoTone';
import ReceiptTwoToneIcon from '@mui/icons-material/ReceiptTwoTone';
import Inventory2TwoToneIcon from '@mui/icons-material/Inventory2TwoTone';
import UndoTwoToneIcon from '@mui/icons-material/UndoTwoTone';
import AddShoppingCartTwoToneIcon from '@mui/icons-material/AddShoppingCartTwoTone';
import HistoryTwoToneIcon from '@mui/icons-material/HistoryTwoTone';
import StorageTwoToneIcon from '@mui/icons-material/StorageTwoTone';
import CompareArrowsTwoToneIcon from '@mui/icons-material/CompareArrowsTwoTone';
import MoveToInboxTwoToneIcon from '@mui/icons-material/MoveToInboxTwoTone';
import AssessmentTwoToneIcon from '@mui/icons-material/AssessmentTwoTone';
import SettingsTwoToneIcon from '@mui/icons-material/SettingsTwoTone';
import AccountCircleTwoToneIcon from '@mui/icons-material/AccountCircleTwoTone';
import SettingsApplicationsTwoToneIcon from '@mui/icons-material/SettingsApplicationsTwoTone';
import DeveloperModeTwoToneIcon from '@mui/icons-material/DeveloperModeTwoTone';
import NotificationsActiveTwoToneIcon from '@mui/icons-material/NotificationsActiveTwoTone';
import SupportAgentTwoToneIcon from '@mui/icons-material/SupportAgentTwoTone';
import KitchenIcon from '@mui/icons-material/Kitchen';
import TrendingUpTwoToneIcon from '@mui/icons-material/TrendingUpTwoTone';
import ScheduleTwoToneIcon from '@mui/icons-material/ScheduleTwoTone';
import ReceiptLongTwoToneIcon from '@mui/icons-material/ReceiptLongTwoTone';
import SavingsTwoToneIcon from '@mui/icons-material/SavingsTwoTone';
import ManageAccountsTwoToneIcon from '@mui/icons-material/ManageAccountsTwoTone';
import PeopleTwoToneIcon from '@mui/icons-material/PeopleTwoTone';
import PaymentsTwoToneIcon from '@mui/icons-material/PaymentsTwoTone';

export interface MenuItem {
  title: string;
  path: string;
  icon: ReactElement;
  roles: string[];
  /** If set, user must have this catalog permission (unless bypass). */
  cloudPosPermission?: CloudPosPermSpec;
  /** Setup entry: any setup-dashboard permission from role. */
  requiresSetupHubAccess?: boolean;
}

export interface MenuGroup {
  subheader: string;
  items: MenuItem[];
}

const menuConfig: MenuGroup[] = [
  {
    subheader: 'menu.mainOperations',
    items: [
      {
        title: 'menu.dashboard',
        path: '/Dashboard',
        icon: <HomeTwoToneIcon />,
        roles: ['Admin','Manager','StoreOwner','Cashier','Staff','CompanyAdmin'],
        cloudPosPermission: { group: 'Main', permission: 'Dashboard' },
      },
      {
        title: 'menu.salesTerminal',
        path: '/sale/create',
        icon: <PointOfSaleTwoToneIcon />,
        roles: ['Admin','Manager','StoreOwner','Cashier','Staff'],
        cloudPosPermission: { group: 'PosOperations', permission: 'PosTerminal' },
      },
      {
        title: 'menu.terminal3',
        path: '/terminal-3/full',
        icon: <PointOfSaleTwoToneIcon />,
        roles: ['Admin','Manager','StoreOwner','Cashier','Staff'],
        cloudPosPermission: { group: 'PosOperations', permission: 'PosTerminal' },
      },
      {
        title: 'menu.workShift',
        path: '/dashboard/work-shift',
        icon: <ScheduleTwoToneIcon />,
        roles: ['Admin','Manager','StoreOwner','Cashier','Staff'],
        cloudPosPermission: { group: 'PosOperations', permission: 'PosTerminal' },
      },
      {
        title: 'menu.kitchenDisplay',
        path: '/dashboard/kitchen',
        icon: <KitchenIcon />,
        roles: ['Admin','Developer','Manager','StoreOwner','Staff','Cashier','CompanyAdmin','Kitchen'],
        cloudPosPermission: { group: 'PosOperations', permission: 'PosTerminal' },
      },
    ],
  },
  {
    subheader: 'menu.salesTransactions',
    items: [
      {
        title: 'menu.salesReturn',
        path: '/sale/return',
        icon: <AssignmentReturnTwoToneIcon />,
        roles: ['Admin','Manager','StoreOwner','Cashier','Staff'],
        cloudPosPermission: { group: 'PosOperations', permission: 'PosTerminal' },
      },
      {
        // Exchange: return + replacement in one transaction (server enforces Exchange:ExchangeCreate).
        title: 'menu.exchange',
        path: '/sale/exchange',
        icon: <SwapHorizTwoToneIcon />,
        roles: ['Admin','Manager','StoreOwner','Cashier','Staff'],
        cloudPosPermission: { group: 'Exchange', permission: 'ExchangeCreate' },
      },
      {
        title: 'menu.salesHistory',
        path: '/sales/getAll',
        icon: <ReceiptTwoToneIcon />,
        roles: ['Admin','Manager','StoreOwner','Cashier','Staff'],
        cloudPosPermission: { group: 'PosOperations', permission: 'SalesHistory' },
      },
      {
        title: 'menu.salesReturnHistory',
        path: '/sales/returnhistory',
        icon: <ReceiptTwoToneIcon />,
        roles: ['Admin','Manager','StoreOwner','Cashier','Staff'],
        cloudPosPermission: { group: 'PosOperations', permission: 'SalesHistory' },
      },
      {
        title: 'menu.exchangeHistory',
        path: '/sales/exchanges',
        icon: <CompareArrowsTwoToneIcon />,
        roles: ['Admin','Manager','StoreOwner','Cashier','Staff'],
        cloudPosPermission: { group: 'Exchange', permission: 'ExchangeView' },
      },
    ],
  },
  {
    subheader: 'menu.inventoryManagement',
    items: [
      {
        title: 'menu.items',
        path: '/items/getAll',
        icon: <Inventory2TwoToneIcon />,
        roles: ['Admin','StoreOwner','Manager'],
        cloudPosPermission: { group: 'InventoryProducts', permission: 'Items' },
      },
      {
        title: 'menu.stock',
        path: '/inventory/stock/items',
        icon: <StorageTwoToneIcon />,
        roles: ['Admin','StoreOwner','Manager'],
        cloudPosPermission: { group: 'InventoryProducts', permission: 'StockItems' },
      },
      {
        title: 'menu.purchaseInventory',
        path: '/inventory/purchase',
        icon: <AddShoppingCartTwoToneIcon />,
        roles: ['Admin','StoreOwner','Manager'],
        cloudPosPermission: { group: 'InventoryProducts', permission: 'PurchaseInventory' },
      },
      {
        title: 'menu.purchaseHistory',
        path: '/inventory/getall',
        icon: <HistoryTwoToneIcon />,
        roles: ['Admin','StoreOwner','Manager'],
        cloudPosPermission: { group: 'InventoryProducts', permission: 'PurchaseHistory' },
      },
      {
        title: 'menu.stockTransfer',
        path: '/inventory/stock/transfer',
        icon: <CompareArrowsTwoToneIcon />,
        roles: ['Admin','StoreOwner','Manager'],
        cloudPosPermission: { group: 'InventoryProducts', permission: 'StockTransfer' },
      },
      {
        title: 'menu.inventoryReturn',
        path: '/inventory/return',
        icon: <UndoTwoToneIcon />,
        roles: ['Admin','StoreOwner','Manager'],
        cloudPosPermission: { group: 'InventoryProducts', permission: 'InventoryReturn' },
      },
      {
        title: 'menu.goodsReceived',
        path: '/inventory/goods-received',
        icon: <MoveToInboxTwoToneIcon />,
        roles: ['Admin','StoreOwner','Manager'],
        cloudPosPermission: { group: 'InventoryProducts', permission: 'GoodsReceivedNote' },
      },
    ],
  },
  {
    subheader: 'menu.financialReports',
    items: [
      {
        title: 'menu.expenseManagement',
        path: '/expense',
        icon: <TrendingUpTwoToneIcon />,
        roles: ['Admin','StoreOwner','Manager'],
        cloudPosPermission: { group: 'FinancialManagement', permission: 'ExpenseManagement' },
      },
      {
        title: 'menu.allReports',
        path: '/reports',
        icon: <AssessmentTwoToneIcon />,
        roles: ['Admin','StoreOwner','Manager','CompanyAdmin'],
        cloudPosPermission: { group: 'Reports', permission: 'ReportsHub' },
      },
      {
        title: 'menu.cashFlowDashboard',
        path: '/reports/cash-flow',
        icon: <SavingsTwoToneIcon />,
        roles: ['Admin','StoreOwner','Manager','CompanyAdmin'],
        cloudPosPermission: { group: 'Reports', permission: 'ReportsHub' },
      },
      {
        // Managers confirm cash handovers by default on the server (no catalog claim needed),
        // so they get the menu item by role as well as by permission.
        title: 'menu.cashManagement',
        path: '/management/setup/cash-management',
        icon: <SavingsTwoToneIcon />,
        roles: ['Admin','StoreOwner','Manager','CompanyAdmin'],
        cloudPosPermission: { group: 'CashManagement', permission: 'CashManagementView' },
      },
    ],
  },
  {
    subheader: 'menu.userManagement',
    items: [
      {
        title: 'menu.roles',
        path: '/management/setup/master/rolelist',
        icon: <ManageAccountsTwoToneIcon />,
        roles: ['Admin', 'StoreOwner', 'Manager', 'CompanyAdmin'],
        cloudPosPermission: { group: 'QuickSetup', permission: 'UserRoles' },
      },
      {
        title: 'menu.userManagement',
        path: '/user-management',
        icon: <PeopleTwoToneIcon />,
        roles: ['Admin', 'StoreOwner', 'Manager', 'CompanyAdmin'],
        cloudPosPermission: { group: 'StaffSecurity', permission: 'UserManagement' },
      },
      {
        title: 'menu.salaryManagement',
        path: '/salary-management',
        icon: <PaymentsTwoToneIcon />,
        roles: ['Admin', 'StoreOwner', 'Manager', 'CompanyAdmin'],
        cloudPosPermission: { group: 'StaffSecurity', permission: 'SalaryManagement' },
      },
    ],
  },
  {
    subheader: 'menu.settingsManagement',
    items: [
      {
        title: 'menu.setup',
        path: '/management/setup/tabs',
        icon: <SettingsTwoToneIcon />,
        roles: ['Admin','StoreOwner','Manager'],
        requiresSetupHubAccess: true,
      },
      {
        title: 'menu.billing',
        path: '/management/billing',
        icon: <ReceiptLongTwoToneIcon />,
        roles: ['Admin', 'StoreOwner', 'Manager', 'CompanyAdmin'],
      },
      {
        title: 'menu.userProfile',
        path: '/management/profile/details',
        icon: <AccountCircleTwoToneIcon />,
        roles: ['Admin','StoreOwner','Manager'],
      },
      {
        title: 'menu.accountSettings',
        path: '/management/profile/settings',
        icon: <SettingsApplicationsTwoToneIcon />,
        roles: ['Admin','StoreOwner','Manager'],
      },
    ],
  },
  {
    subheader: 'menu.developerTools',
    items: [
      {
        title: 'menu.developerDashboard',
        path: '/developer/dashboard',
        icon: <DeveloperModeTwoToneIcon />,
        roles: ['Developer'],
      },
      {
        title: 'menu.notificationsCenter',
        path: '/developer/notifications',
        icon: <NotificationsActiveTwoToneIcon />,
        roles: ['Developer'],
      },
    ],
  },
];

export default menuConfig;